import type {
	ExternalFormData,
	Form360Data,
	StandardFormData,
} from "../../../../../../../components/FormSelectionCard";

import type { ConfiguredForm, FormsByType } from "../types";

import type { TemplateFormAssignmentWithNames } from "../../../../../../../api/services/defs/review-templates";

type WithNames = TemplateFormAssignmentWithNames;
type InternalAssignment = Extract<WithNames, { basic: true }>;
type ExternalAssignment = Extract<WithNames, { external: true }>;
type EasyAddAssignment = Extract<WithNames, { easyAdd: true }>;

const generateLocalId = () => crypto.randomUUID();

const WHO_CAN_ADD_FORM: Record<NonNullable<EasyAddAssignment["whoCanEasyAdd"]>, Form360Data["whoCanAddForm"]> = {
	"any-employee": "Any Employee",
	subject: "Subject",
	"manager-chain-of-command": "Manager",
	admin: "Admin",
};

const APPROVAL_LABEL: Record<Exclude<EasyAddAssignment["requiresApproval"], null>, Form360Data["approval"]> = {
	"managers-and-admins": "Require Approval from Admins or Managers",
	"admins-only": "Require Approval from Admins",
};

const transformInternal = (a: InternalAssignment): ConfiguredForm<"standard"> => {
	const data: StandardFormData = {
		formType: "standard",
		formName: a.formName ?? "",
		formAuthors: a.authors.map((p) => ({ id: String(p.id), name: p.name })),
		formSigners: (a.signers ?? []).map((p) => ({ id: String(p.id), name: p.name })),
		authorsDueDate: a.daysToCompleteAuthors,
		signersDueDate: a.daysToCompleteSigners,
		isRelativeDates: true,
		makeVisibleWhenCompleted: a.visible,
		redactAuthors: a.redacted,
	};

	return {
		localId: generateLocalId(),
		formId: String(a.formBaseId),
		fromTemplate: true,
		data,
	};
};

const transformExternal = (a: ExternalAssignment): ConfiguredForm<"external"> => {
	const firstAuthor = a.authors?.[0];
	const data: ExternalFormData = {
		formType: "external",
		formName: a.formName ?? "",
		reviewerName: firstAuthor?.name || undefined,
		reviewerEmail: firstAuthor?.email || undefined,
		emailMessage: firstAuthor?.emailBody || undefined,
		makeVisibleWhenFinished: a.visible,
		redactAuthors: a.redacted,
	};

	return {
		localId: generateLocalId(),
		formId: String(a.formBaseId),
		fromTemplate: true,
		data,
	};
};

const transformEasyAdd = (a: EasyAddAssignment): ConfiguredForm<"360"> => {
	const whoCanAdd = a.whoCanEasyAdd ? WHO_CAN_ADD_FORM[a.whoCanEasyAdd] : "Any Employee";
	const data: Form360Data = {
		formType: "360",
		formName: a.formName ?? "",
		whoCanAddForm: whoCanAdd,
		formAuthor:
			whoCanAdd === "Subject"
				? a.subjectEmployeeChooseAuthor === "any-employee"
					? "Any Employee"
					: "Subject"
				: undefined,
		approval: a.requiresApproval ? APPROVAL_LABEL[a.requiresApproval] : "No Approval Needed",
		formSigners: (a.signers ?? []).map((p) => ({ id: String(p.id), name: p.name })),
		authorsDueDate: a.daysToCompleteAuthors,
		signersDueDate: a.daysToCompleteSigners,
		isRelativeDates: true,
		dueDatesToggle: false,
		makeVisibleWhenFinished: a.visible,
		allow360RequestMessage: a.allowComments ?? false,
		redactAuthors: a.redacted,
		makeFormInactive: a.isActive === false,
	};

	return {
		localId: generateLocalId(),
		formId: String(a.formBaseId),
		fromTemplate: true,
		data,
	};
};

const isInternal = (a: WithNames): a is InternalAssignment => "basic" in a && a.basic === true;
const isExternal = (a: WithNames): a is ExternalAssignment => "external" in a && a.external === true;
const isEasyAdd = (a: WithNames): a is EasyAddAssignment => "easyAdd" in a && a.easyAdd === true;

/**
 * Map the v2 detail-endpoint shape (`TemplateFormAssignmentWithNames[]`) into our ConfiguredForm
 * buckets. Inactive 360 forms are filtered out.
 */
export const templateAssignmentsToConfiguredForms = (assignments: WithNames[]): FormsByType => {
	const buckets: FormsByType = { standard: [], "360": [], external: [] };

	for (const a of assignments) {
		if (isExternal(a)) {
			buckets.external.push(transformExternal(a));
		} else if (isEasyAdd(a)) {
			if (a.isActive !== false) buckets["360"].push(transformEasyAdd(a));
		} else if (isInternal(a)) {
			buckets.standard.push(transformInternal(a));
		}
	}

	return buckets;
};

export { generateLocalId };
